import { type IconName, type PluginInclude } from '@grafana/data';
import { GrafanaEdition } from '@grafana/data/internal';
import { config } from '@grafana/runtime';
import {
  APP_OBSERVABILITY_APP_ID,
  ASSERTS_APP_ID,
  ASSISTANT_APP_ID,
  AUTH_APP_ID,
  HOSTED_TRACES_APP_ID,
  LOGS_DRILLDOWN_APP_ID,
  METRICS_DRILLDOWN_APP_ID,
  SERVICECENTER_APP_ID,
  SLO_APP_ID,
  SYNTHETIC_MONITORING_APP_ID,
} from 'app/core/appPluginIds';

import { NavID, type NavId, NavWeight } from './constants';

export interface AppNavConfig {
  /** Required unless the entry is `hidden`, which has nowhere to be placed */
  sectionId?: NavId;
  sortWeight?: number;
  text?: string;
  icon?: IconName;
  subTitle?: string;
  isNew?: boolean;
  /**
   * Render the app's pages as standalone entries of the target section instead
   * of nesting them under an app node (asserts today; the assistant plans the
   * same). `slotWeightByPath` pins specific pages to a section slot; the rest
   * sort above the section's app entries in their own order.
   */
  hoistPages?: { slotWeightByPath?: Record<string, number> };
  /** Drops the `includes` that the app should not show in this deployment, on top of the RBAC checks */
  filterInclude?: (include: PluginInclude) => boolean;
  /**
   * Keeps the app, or the page, out of the nav altogether — for something a
   * deployment installs but does not surface, or surfaces only through pages
   * placed individually (hiding an app does not hide those). The INI overrides
   * express this by naming a section that does not exist; here it is explicit.
   */
  hidden?: boolean;
}

export const ASSERTS_SERVICES_PATH = `/a/${ASSERTS_APP_ID}/services`;
export const SLO_SERVICES_PATH = `/a/${SLO_APP_ID}/services`;

// The assistant pages OSS deployments get (mirrors assistantOSSNavigationPaths
// in the Go builder)
const ASSISTANT_OSS_NAV_PATHS = new Set([
  `/a/${ASSISTANT_APP_ID}`,
  `/a/${ASSISTANT_APP_ID}/workspace`,
  `/a/${ASSISTANT_APP_ID}/settings`,
]);

// The asserts services page takes the same Observability slot as the App
// Observability entry — only one of the two is ever shown. The value mirrors
// the weight in the Go builder's table (applinks.go), as every weight here does.
const APP_OBSERVABILITY_SORT_WEIGHT = 5;

/**
 * Where known app plugins go in the nav. An app with no entry here is listed
 * under "More apps" by its plugin.json name, so add one only to place the app
 * somewhere specific or to change how it reads there:
 *
 * ```
 * 'myorg-widgets-app': {
 *   // Which section to appear in. NavID.root makes it a top-level item.
 *   sectionId: NavID.observability,
 *   // Position within the section — lower sorts higher.
 *   sortWeight: 6,
 *   // Shown instead of the plugin.json name. Also add a matching
 *   // `plugin-page-myorg-widgets-app` case to navBarItem-translations.ts:
 *   // display text is translated by nav id, and that lookup wins over this.
 *   text: 'Widgets',
 *   // Override the plugin's own icon and description in the same way.
 *   icon: 'apps',
 *   subTitle: 'Monitor your widgets',
 *   // Adds the "New" badge.
 *   isNew: true,
 * },
 * ```
 *
 * `hoistPages` and `filterInclude` cover the rarer cases and are documented on
 * AppNavConfig above. Keep entries in step with the equivalent
 * table in the Go builder (applinks.go), which serves the same nav when the
 * client-built tree is off.
 */
const APP_NAV_CONFIG: Record<string, AppNavConfig> = {
  ///////////////////
  // Observability //
  ///////////////////
  'grafana-agento11y-app': {
    sectionId: NavID.observability,
    sortWeight: 1,
    text: 'Agent',
    isNew: true,
  },
  [ASSERTS_APP_ID]: {
    sectionId: NavID.observability,
    sortWeight: 3,
    icon: 'asserts',
    hoistPages: { slotWeightByPath: { [ASSERTS_SERVICES_PATH]: APP_OBSERVABILITY_SORT_WEIGHT } },
  },
  'grafana-kowalski-app': {
    sectionId: NavID.observability,
    sortWeight: 4,
    text: 'Frontend',
  },
  [APP_OBSERVABILITY_APP_ID]: {
    sectionId: NavID.observability,
    sortWeight: APP_OBSERVABILITY_SORT_WEIGHT,
    text: 'Application',
  },
  'grafana-dbo11y-app': {
    sectionId: NavID.observability,
    sortWeight: 6,
    text: 'Database',
  },
  'grafana-k8s-app': {
    sectionId: NavID.observability,
    sortWeight: 7,
    text: 'Kubernetes',
  },
  'grafana-csp-app': {
    sectionId: NavID.observability,
    sortWeight: 8,
    icon: 'cloud-provider',
  },

  ///////////////
  // Drilldown //
  ///////////////
  [METRICS_DRILLDOWN_APP_ID]: {
    sectionId: NavID.drilldown,
    sortWeight: 1,
    text: 'Metrics',
  },
  [LOGS_DRILLDOWN_APP_ID]: {
    sectionId: NavID.drilldown,
    sortWeight: 2,
    text: 'Logs',
  },
  [HOSTED_TRACES_APP_ID]: {
    sectionId: NavID.drilldown,
    sortWeight: 3,
    text: 'Traces',
  },
  'grafana-pyroscope-app': {
    sectionId: NavID.drilldown,
    sortWeight: 4,
    text: 'Profiles',
  },
  'grafana-sqldrilldown-app': {
    sectionId: NavID.drilldown,
    sortWeight: 5,
    text: 'SQL',
  },

  //////////////////////////
  // Testing & synthetics //
  //////////////////////////
  'grafana-agentictesting-app': {
    sectionId: NavID.testingAndSynthetics,
    sortWeight: 1,
    text: 'Agentic testing',
    isNew: true,
  },
  'k6-app': {
    sectionId: NavID.testingAndSynthetics,
    sortWeight: 2,
    text: 'Performance',
  },
  [SYNTHETIC_MONITORING_APP_ID]: {
    sectionId: NavID.testingAndSynthetics,
    sortWeight: 3,
    text: 'Synthetics',
  },
  'grafana-e2e-test-o11y-app': {
    sectionId: NavID.testingAndSynthetics,
    sortWeight: 4,
    text: 'E2E test observability',
    isNew: true,
  },

  //////////////////
  // Alerts & IRM //
  //////////////////
  [SERVICECENTER_APP_ID]: {
    sectionId: NavID.alertsAndIncidents,
    sortWeight: 1,
    text: 'Service Center',
  },
  'grafana-irm-app': {
    sectionId: NavID.alertsAndIncidents,
    sortWeight: 3,
    text: 'IRM',
  },
  [SLO_APP_ID]: {
    sectionId: NavID.alertsAndIncidents,
    sortWeight: 4,
  },
  'grafana-labelmanagement-app': {
    sectionId: NavID.alertsAndIncidents,
    sortWeight: 5,
    text: 'Label management',
  },
  'grafana-incident-app': {
    sectionId: NavID.alertsAndIncidents,
    sortWeight: 6,
    text: 'Incident',
  },
  'grafana-oncall-app': {
    sectionId: NavID.alertsAndIncidents,
    sortWeight: 7,
    text: 'OnCall',
  },

  ////////////////////////
  // Top-level sections //
  ////////////////////////
  [ASSISTANT_APP_ID]: {
    sectionId: NavID.root,
    sortWeight: NavWeight.assistant,
    text: 'AI',
    subTitle: 'AI-powered assistant for Grafana',
    icon: 'ai-sparkle',
    // Enterprise and cloud stacks show every assistant page; OSS deployments
    // only the core ones. The Go builder additionally reads per-org plugin
    // jsonData, which is not readable client-side: an `ossMode` stack is
    // restricted to the OSS paths server-side but shows every page here, and
    // trial-mode orgs are restricted to a narrower set than we apply. Both are
    // cloud-only, so they over-show rather than hide anything.
    filterInclude: (include) =>
      config.buildInfo.edition !== GrafanaEdition.OpenSource ||
      config.namespace.startsWith('stacks-') ||
      (include.path !== undefined && ASSISTANT_OSS_NAV_PATHS.has(include.path)),
  },
  'grafana-ml-app': {
    sectionId: NavID.root,
    sortWeight: NavWeight.aiAndMl,
    text: 'Machine Learning',
    subTitle: 'Explore AI and machine learning features',
    icon: 'gf-ml-alt',
  },
  'grafana-workflows-app': {
    sectionId: NavID.root,
    sortWeight: NavWeight.workflows,
    text: 'Workflows',
    subTitle: 'Create and manage automated workflows',
    icon: 'workflows',
  },
  'grafana-cmab-app': {
    sectionId: NavID.root,
    sortWeight: NavWeight.cmab,
    icon: 'cmab-logo',
  },
  'grafana-easystart-app': {
    sectionId: NavID.root,
    sortWeight: NavWeight.apps + 1,
    text: 'Connections',
    icon: 'adjust-circle',
  },

  ////////////////////////
  // Adaptive Telemetry //
  ////////////////////////
  'grafana-adaptive-metrics-app': {
    sectionId: NavID.adaptiveTelemetry,
    sortWeight: 1,
  },
  'grafana-adaptivelogs-app': {
    sectionId: NavID.adaptiveTelemetry,
    sortWeight: 2,
  },
  'grafana-adaptivetraces-app': {
    sectionId: NavID.adaptiveTelemetry,
    sortWeight: 3,
  },
  'grafana-adaptiveprofiles-app': {
    sectionId: NavID.adaptiveTelemetry,
    sortWeight: 4,
  },

  ////////////////////
  // Administration //
  ////////////////////
  'grafana-cloud-link-app': {
    sectionId: NavID.cfgPlugins,
    sortWeight: 3,
  },
  'grafana-advisor-app': {
    sectionId: NavID.cfg,
    sortWeight: 0,
    text: 'Advisor',
    subTitle: 'Run checks and get suggestions to fix issues',
  },
};

/**
 * Where an individual app page goes, keyed by its include path. A page listed
 * here is lifted out of its app and placed on its own, which is how Pages sits
 * beside Dashboards and how the auth app's single page becomes a leaf under
 * Users and access. Mirrors navigationAppPathConfig in the Go builder.
 */
const APP_NAV_PATH_CONFIG: Record<string, AppNavConfig> = {
  '/a/grafana-agento11y-app/coding-agents': {
    sectionId: NavID.observability,
    sortWeight: 2,
    text: 'Coding agent',
    isNew: true,
  },
  [`/a/${ASSISTANT_APP_ID}/pages`]: {
    sectionId: NavID.root,
    sortWeight: NavWeight.dashboards + 1,
    icon: 'pages',
    isNew: true,
  },
  [`/a/${AUTH_APP_ID}`]: {
    sectionId: NavID.cfgAccess,
    sortWeight: 2,
    subTitle: 'Use policies to control automated access to metrics, logs, traces, and other Grafana Cloud services',
  },
};

/**
 * The nav config for an app plugin: the built-in entry with any operator
 * [navigation.app_sections] override laid over it. Same merge as the Go
 * builder's readNavigationSettings — the override wins on placement, its weight
 * only when non-zero, and display overrides, hoisting and include filtering
 * stay built-in.
 */
export function appNavConfigFor(appId: string): AppNavConfig | undefined {
  const builtIn = APP_NAV_CONFIG[appId];
  const override = config.navigation.appSections?.[appId];
  if (!override) {
    return builtIn;
  }
  return {
    ...builtIn,
    // Naming a section says where the app goes, so it also undoes a built-in
    // hide: an instance that wants a hidden app back can ask for it
    hidden: false,
    sectionId: toSectionId(override.sectionId),
    sortWeight: override.sortWeight || builtIn?.sortWeight || 0,
  };
}

/**
 * The nav config for a standalone app page. Unlike the app config above, an
 * operator [navigation.app_standalone_pages] override replaces the built-in
 * entry outright, as it does server-side.
 */
export function appNavPathConfigFor(includePath: string): AppNavConfig | undefined {
  const override = config.navigation.appStandalonePages?.[includePath];
  return override
    ? { sortWeight: 0, ...override, sectionId: toSectionId(override.sectionId) }
    : APP_NAV_PATH_CONFIG[includePath];
}

/** Whether a section id came from an operator override rather than our own tables */
export function isOperatorPlacedSection(sectionId: string): boolean {
  const { appSections = {}, appStandalonePages = {} } = config.navigation;
  return [...Object.values(appSections), ...Object.values(appStandalonePages)].some(
    (placement) => placement.sectionId === sectionId
  );
}

/**
 * The one place an unchecked section id enters: an operator can name any
 * section, including one that exists nowhere, which is how an app or page gets
 * hidden. Our own tables stay typed against NavId.
 */
function toSectionId(sectionId: string): NavId {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return sectionId as NavId;
}
